import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  moment,
} from "../../../Collections/Imports";
import Alert from "@mui/material/Alert";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useApiCallMutation } from "../../../Services/ApiCallService";
import { campaignModuleAPiEndPoints, pingtreeApiEndPoints } from "../../../Config/APIsEndpoints";
import CircularProgressBar from "../../../Components/UIComponents/CircularProgressBar";
import useUnsavedChangesGuard from "../../../CustomHooks/useUnsavedChangesGuard";
import TierGroups from "../../../Forms/TieredTreeBuilder/TierGroups";
import SaveDialog from "../../../Forms/TieredTreeBuilder/SaveDialog";
import { VersionHistoryPanel, WalkOrderPanel } from "../../../Forms/TieredTreeBuilder/SidePanels";
import {
  addCampaign,
  buildImportDocument,
  buyersOf,
  apiErrorText,
  isLdsAdmin,
  exportFileName,
  fingerprint,
  formatMoney,
  groupTree,
  moveInList,
  mapServerErrors,
  moveTier,
  moveToTierEdge,
  moveToTreeBottom,
  moveToTreeTop,
  moveWithinTier,
  parseTreeFile,
  removeCampaign,
  renumberTiers,
  setField,
  slotsFromDocument,
  toSlot,
  TREE_SETTING_FIELDS,
  validateSlots,
  walkOrder,
} from "../../../Forms/TieredTreeBuilder/treeLogic";

const readUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem("userInfo"));
  } catch (e) {
    return null;
  }
};

const downloadJson = (doc, fileName) => {
  const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const campaignTypeFor = (treeType) =>
  String(treeType).toLowerCase() === "silent" ? "silence" : String(treeType).toLowerCase() === "redirect" ? "redirect" : undefined;

/**
 * Tiered ping-tree builder (CU-LS-M1-057): tiers as groups, add/remove/move campaigns, the effective walk order,
 * import/export as JSON. Saves through POST /tree/import (dry run first, then apply; all or nothing).
 */
const TreeBuilderEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const loadGeneration = useRef(0);

  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [tree, setTree] = useState(null);
  const [manualOrder, setManualOrder] = useState(false);
  const [slots, setSlots] = useState([]);
  const [revision, setRevision] = useState(null);
  const [saved, setSaved] = useState(null); // fingerprint of the saved tree
  const [knownCampaigns, setKnownCampaigns] = useState([]);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [campaignsVersion, setCampaignsVersion] = useState(0); // bumped after a save to refresh the picker
  const [filters, setFilters] = useState({ hideInactive: false, buyerId: "", minPrice: "", maxPrice: "" });
  const [leadPrice, setLeadPrice] = useState("");
  const [message, setMessage] = useState(null); // {severity, text, lines?}
  const [serverErrors, setServerErrors] = useState({ byCampaign: {}, general: [] });
  const [toAdd, setToAdd] = useState(null);
  const [addTier, setAddTier] = useState("");
  const [dryRun, setDryRun] = useState(null); // {result, doc}
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);

  const [callExport] = useApiCallMutation();
  const [callImport] = useApiCallMutation();
  const [callCampaigns] = useApiCallMutation();

  const loadTree = useCallback(async () => {
    ++loadGeneration.current;
    setDryRun(null);
    setLoadState({ loading: true, error: null });
    try {
      const body = await callExport({ endPoint: pingtreeApiEndPoints.exportTree(id), method: "GET" }).unwrap();
      if (body?.error) {
        setLoadState({ loading: false, error: typeof body.data === "string" ? body.data : "Could not load the tree" });
        return null;
      }
      const doc = body.data;
      const loadedTree = { ...doc.tree, ...(doc.tailReservations?.length ? { tailReservations: doc.tailReservations } : {}) };
      const loadedSlots = (doc.campaigns || []).map(toSlot);
      setTree(loadedTree);
      setManualOrder(!!doc.tree?.manualOrder);
      setSlots(loadedSlots);
      setPendingInputs({});
      setRevision(doc.revision);
      setSaved(fingerprint({ tree: loadedTree, slots: loadedSlots, manualOrder: !!doc.tree?.manualOrder }));
      setServerErrors({ byCampaign: {}, general: [] });
      setConflict(false);
      if ((doc.missingCampaignIds || []).length) {
        setMessage({
          severity: "warning",
          text: `${doc.missingCampaignIds.length} campaign id(s) in this tree no longer exist; saving drops them.`,
        });
      }
      setLoadState({ loading: false, error: null });
      return doc;
    } catch (e) {
      setLoadState({ loading: false, error: apiErrorText(e, "Could not load the tree") });
      return null;
    }
  }, [callExport, id]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  // Campaigns that can be added: same type as the tree (redirect / silence).
  useEffect(() => {
    if (!tree?.pingTreeType) return;
    callCampaigns({
      endPoint: campaignModuleAPiEndPoints.getAllCampaigns,
      method: "POST",
      data: {
        data: {
          index: "All",
          itemPerIndex: "All",
          buyerId: ["All"],
          campaignName: ["All"],
          campaignTagsId: ["All"],
          labelId: ["All"],
          // every type: an exported tree may hold a campaign of the other type (the API only warns)
          status: ["All"],
          fromDate: new Date(0), // every campaign: the API filters on createdOn
          toDate: moment().endOf("day").toDate(),
        },
      },
    })
      .unwrap()
      .then((body) => setKnownCampaigns(Array.isArray(body?.data?.campaigns) ? body.data.campaigns : []))
      .catch(() => setKnownCampaigns([]))
      .finally(() => setCampaignsLoaded(true));
  }, [callCampaigns, tree?.pingTreeType, campaignsVersion]);

  // A rank input that was typed into but not yet committed (blur/Enter) also counts as an unsaved change.
  const [pendingInputs, setPendingInputs] = useState({});
  const onPending = useCallback((key, pending) => {
    setPendingInputs((p) => {
      if (!!p[key] === pending) return p;
      const next = { ...p };
      if (pending) next[key] = true;
      else delete next[key];
      return next;
    });
  }, []);
  const current = tree === null ? null : fingerprint({ tree, slots, manualOrder });
  const latest = useRef(current);
  latest.current = current;
  // typed-but-uncommitted rank inputs, readable after an async check (state would be stale in that closure)
  const pendingRef = useRef(pendingInputs);
  pendingRef.current = pendingInputs;
  const dirty = (saved !== null && current !== null && current !== saved) || Object.keys(pendingInputs).length > 0;
  useUnsavedChangesGuard(dirty);

  const clientErrors = useMemo(() => validateSlots(slots), [slots]);
  const rowErrors = useMemo(() => {
    const merged = { ...serverErrors.byCampaign };
    Object.entries(clientErrors).forEach(([cid, e]) => {
      merged[cid] = { ...(merged[cid] || {}), ...e };
    });
    return merged;
  }, [clientErrors, serverErrors]);

  const preview = leadPrice === "" ? null : leadPrice;
  const walk = useMemo(() => walkOrder(slots, { manualOrder, leadPrice: preview }), [slots, manualOrder, preview]);
  const positions = useMemo(() => Object.fromEntries(walk.map((s, i) => [s.campaignId, i + 1])), [walk]);
  const groups = useMemo(() => groupTree(slots, { manualOrder, leadPrice: preview }), [slots, manualOrder, preview]);
  const buyers = useMemo(() => buyersOf(slots), [slots]);
  const addable = useMemo(
    () =>
      knownCampaigns.filter(
        (c) =>
          String(c.campaignType).toLowerCase() === campaignTypeFor(tree?.pingTreeType) &&
          !slots.some((s) => s.campaignId === (c.campaignId ?? c.id))
      ),
    [knownCampaigns, slots, tree?.pingTreeType]
  );

  const change = (result) => {
    if (result.error) {
      setMessage({ severity: "info", text: result.error });
      return;
    }
    setSlots(result.slots);
    setMessage(result.note ? { severity: "info", text: result.note } : null);
  };

  const onAction = (action, campaignId) => {
    switch (action) {
      case "up":
      case "down":
        return change(moveWithinTier(slots, campaignId, action, preview));
      case "tierTop":
        return change(moveToTierEdge(slots, campaignId, "top", preview));
      case "tierBottom":
        return change(moveToTierEdge(slots, campaignId, "bottom", preview));
      case "treeTop":
        return change(moveToTreeTop(slots, campaignId));
      case "treeBottom":
        return change(moveToTreeBottom(slots, campaignId));
      case "listUp":
        return change(moveInList(slots, campaignId, "up"));
      case "listDown":
        return change(moveInList(slots, campaignId, "down"));
      case "listTop":
        return change(moveInList(slots, campaignId, "top"));
      case "listBottom":
        return change(moveInList(slots, campaignId, "bottom"));
      case "remove":
        return change({ slots: removeCampaign(slots, campaignId), error: null });
      default:
        return undefined;
    }
  };

  const onAdd = () => {
    const result = addCampaign(slots, toAdd, addTier);
    change(result);
    if (!result.error) {
      setToAdd(null);
    }
  };

  const onExport = async () => {
    const body = await callExport({ endPoint: pingtreeApiEndPoints.exportTree(id), method: "GET" })
      .unwrap()
      .catch(() => null);
    if (!body || body.error) {
      setMessage({ severity: "error", text: "Export failed" });
      return;
    }
    downloadJson(body.data, exportFileName(body.data.tree));
    if (dirty) setMessage({ severity: "info", text: "Downloaded the saved tree. Unsaved edits on this screen are not in the file." });
  };

  const onFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const { doc, errors } = parseTreeFile(String(reader.result));
      if (errors.length) {
        setMessage({ severity: "error", text: `The file was not loaded (${errors.length} problem(s)).`, lines: errors.map((e) => `${e.path}: ${e.message}`) });
        return;
      }
      if (doc.tree.pingTreeType !== undefined && String(doc.tree.pingTreeType).toLowerCase() !== String(tree.pingTreeType).toLowerCase()) {
        setMessage({ severity: "error", text: `The file is a ${doc.tree.pingTreeType} tree; this is a ${tree.pingTreeType} tree.` });
        return;
      }
      const resolved = slotsFromDocument(doc, knownCampaigns);
      if (resolved.errors.length) {
        setMessage({ severity: "error", text: "The file was not loaded.", lines: resolved.errors.map((e) => `${e.path}: ${e.message}`) });
        return;
      }
      setTree((t) => {
        const imported = { ...t };
        // Omitted advanced settings must remain omitted on Save, rather than replay a previously loaded value.
        TREE_SETTING_FIELDS.forEach((field) => {
          delete imported[field];
          if (Object.prototype.hasOwnProperty.call(doc.tree, field)) imported[field] = doc.tree[field];
        });
        return {
          ...imported,
          name: typeof doc.tree.name === "string" ? doc.tree.name : t.name,
          comment: typeof doc.tree.comment === "string" ? doc.tree.comment : t.comment,
          active: typeof doc.tree.active === "boolean" ? doc.tree.active : t.active,
        };
      });
      if (typeof doc.tree.manualOrder === "boolean") setManualOrder(doc.tree.manualOrder);
      setSlots(resolved.slots);
      setServerErrors({ byCampaign: {}, general: [] });
      const fromOther = doc.tree.treeUniqueId !== undefined && String(doc.tree.treeUniqueId) !== String(tree.treeUniqueId);
      setMessage({
        severity: "info",
        text: `Loaded ${resolved.slots.length} campaigns from ${file.name}${fromOther ? ` (exported from tree ${doc.tree.treeUniqueId})` : ""}.${
          resolved.unresolved ? ` ${resolved.unresolved} are not in the loaded campaign list; Save checks them.` : ""
        } Nothing is saved until you press Save.`,
      });
    };
    reader.readAsText(file);
  };

  const onSave = async () => {
    if (Object.keys(pendingRef.current).length) {
      setMessage({ severity: "info", text: "Finish editing (press Enter or leave the field), then press Save." });
      return;
    }
    if (Object.keys(clientErrors).length) {
      setMessage({ severity: "error", text: "Fix the highlighted fields first." });
      return;
    }
    const doc = buildImportDocument({ tree, slots, manualOrder, baseRevision: revision, dryRun: true });
    const checkedFingerprint = current;
    const checkedGeneration = loadGeneration.current;
    setSaving(true);
    let failed = null;
    const body = await callImport({ endPoint: pingtreeApiEndPoints.importTree, method: "POST", data: { data: doc } })
      .unwrap()
      .catch((e) => {
        failed = e;
        return null;
      });
    setSaving(false);
    if (checkedGeneration !== loadGeneration.current) return;
    if (!body) {
      setMessage({ severity: "error", text: apiErrorText(failed, "Save check failed: the API did not answer.") });
      return;
    }
    if (body.error) {
      handleImportError(body, doc);
      return;
    }
    setServerErrors({ byCampaign: {}, general: [] });
    // an edit typed while the check ran (committed or still in its input) must not be dropped by the save
    if (latest.current !== checkedFingerprint || Object.keys(pendingRef.current).length) {
      setMessage({ severity: "info", text: "The tree changed while it was being checked. Press Save again." });
      return;
    }
    setDryRun({ result: body.data, doc });
  };

  const handleImportError = (body, doc) => {
    if (body?.status?.code === 409) {
      setConflict(true);
      setMessage({ severity: "error", text: body.data?.message || "The tree changed since it was loaded." });
      return;
    }
    const mapped = mapServerErrors(body.data?.errors, doc.campaigns);
    setServerErrors(mapped);
    setMessage({
      severity: "error",
      text: typeof body.data === "string" ? body.data : body.data?.message || "Save failed",
      lines: mapped.general,
    });
  };

  const onConfirmSave = async () => {
    const doc = { ...dryRun.doc, dryRun: false };
    setSaving(true);
    let failed = null;
    const body = await callImport({ endPoint: pingtreeApiEndPoints.importTree, method: "POST", data: { data: doc } })
      .unwrap()
      .catch((e) => {
        failed = e;
        return null;
      });
    if (!body || body.error) {
      setSaving(false);
      setDryRun(null);
    }
    if (!body) {
      setMessage({ severity: "error", text: apiErrorText(failed, "Save failed: the API did not answer. Reload to see what is stored.") });
      return;
    }
    if (body.error) {
      handleImportError(body, doc);
      return;
    }
    // The dialog stays open (modal, "Saving…") until the saved tree is reloaded, so no edit can slip in between.
    await loadTree();
    setCampaignsVersion((v) => v + 1);
    setSaving(false);
    setDryRun(null);
    setMessage({ severity: "success", text: `Saved. ${body.data?.walkOrder?.length ?? 0} campaigns in the tree.` });
  };

  if (loadState.loading) return <CircularProgressBar />;
  if (loadState.error) {
    return (
      <Box p={2}>
        <Alert severity="error">{loadState.error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/ping-tree")}>
          Back to ping trees
        </Button>
      </Box>
    );
  }

  return (
    <Box p={2} data-testid="tree-builder">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {tree.name}
        </Typography>
        <Chip size="small" label={tree.pingTreeType} />
        <Chip size="small" color={tree.active ? "success" : "default"} label={tree.active ? "Active" : "Inactive"} />
        <Chip size="small" variant="outlined" label={`${slots.length} campaigns · ${buyers.length} buyers`} />
        {buyers.length < 10 && <Chip size="small" variant="outlined" color="warning" label="Launch target: at least 10 buyers" />}
        {dirty && <Chip size="small" color="warning" label="Unsaved changes" />}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={onExport}>
          Export JSON
        </Button>
        {(
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileUploadOutlinedIcon />}
              disabled={!campaignsLoaded}
              title={campaignsLoaded ? "" : "Loading campaigns…"}
              onClick={() => fileInput.current?.click()}
            >
              Import JSON
            </Button>
            <input ref={fileInput} type="file" accept="application/json,.json" hidden onChange={onFile} data-testid="tree-file" />
            <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} disabled={!dirty && !conflict} onClick={loadTree}>
              {conflict ? "Reload" : "Discard changes"}
            </Button>
            <Button size="small" variant="contained" style={{ color: "#fff" }} startIcon={<SaveOutlinedIcon />} disabled={!dirty || saving} onClick={onSave}>
              Save
            </Button>
          </>
        )}
      </Stack>
      {tree.comment && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {tree.comment}
        </Typography>
      )}

      {message && (
        <Alert severity={message.severity} sx={{ mb: 1 }} onClose={() => setMessage(null)}>
          {message.text}
          {(message.lines || []).map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
          <FormControlLabel
            control={<Switch checked={filters.hideInactive} onChange={(e) => setFilters({ ...filters, hideInactive: e.target.checked })} />}
            label="Hide paused/capped"
          />
          <TextField
            select
            size="small"
            label="Buyer"
            value={filters.buyerId}
            onChange={(e) => setFilters({ ...filters, buyerId: e.target.value })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All buyers</MenuItem>
            {buyers.map((b) => (
              <MenuItem key={b.buyerId} value={b.buyerId}>
                {b.buyerName}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Min $" value={filters.minPrice} onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })} sx={{ width: 90 }} />
          <TextField size="small" label="Max $" value={filters.maxPrice} onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })} sx={{ width: 90 }} />
          <TextField
            size="small"
            label="Preview lead price $"
            value={leadPrice}
            onChange={(e) => setLeadPrice(e.target.value)}
            sx={{ width: 160 }}
            helperText={leadPrice ? `Unpriced campaigns rank at ${formatMoney(leadPrice)}` : " "}
          />
          {(
            <FormControlLabel
              control={<Switch checked={!!tree.active} onChange={(e) => setTree({ ...tree, active: e.target.checked })} />}
              label="Tree active"
            />
          )}
          {(
            <FormControlLabel
              control={<Switch checked={manualOrder} onChange={(e) => setManualOrder(e.target.checked)} />}
              label="Manual order (ignore tiers)"
            />
          )}
          {!manualOrder && (
            <Button size="small" onClick={() => setSlots(renumberTiers(slots))}>
              Renumber tiers 1..n
            </Button>
          )}
        </Stack>
        {(
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 360 }}
              options={addable}
              value={toAdd}
              onChange={(e, v) => setToAdd(v)}
              getOptionLabel={(c) => `${c.buyerName || "?"} · ${c.campaignName} #${c.uniqueId} · ${formatMoney(c.price)}`}
              isOptionEqualToValue={(a, b) => (a.campaignId ?? a.id) === (b.campaignId ?? b.id)}
              renderInput={(params) => <TextField {...params} label={`Add a ${campaignTypeFor(tree.pingTreeType)} campaign`} />}
            />
            <TextField size="small" label="Tier" value={addTier} onChange={(e) => setAddTier(e.target.value)} sx={{ width: 80 }} helperText="blank = keep" />
            <Button variant="outlined" onClick={onAdd} disabled={!toAdd}>
              Add campaign
            </Button>
          </Stack>
        )}
      </Paper>

      {manualOrder && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Manual order is on: LDS keeps list order within posted buyers and within exit offers, with exit offers last.
          Tiers, prices and priorities are ignored. Use the arrows to reorder; turn it off to build the tree in tiers.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TierGroups
            groups={groups}
            positions={positions}
            filters={filters}
            readOnly={false}
            errors={rowErrors}
            onAction={onAction}
            onField={(campaignId, field, value) => setSlots(setField(slots, campaignId, field, value))}
            onPending={onPending}
            onMoveTier={(t, direction, exitOffer) => change(moveTier(slots, t, direction, exitOffer))}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            <WalkOrderPanel walk={walk} manualOrder={manualOrder} leadPrice={preview} tierOrder={tree.tierOrder} />
            <VersionHistoryPanel currentVersion={tree.version} />
          </Stack>
        </Grid>
      </Grid>

      <SaveDialog open={!!dryRun} result={dryRun?.result} saving={saving} onCancel={() => setDryRun(null)} onConfirm={onConfirmSave} />
    </Box>
  );
};

/** Only LDS admins get the builder; the API enforces the same rule (403 for editors and viewers). */
const TreeBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  if (!isLdsAdmin(readUserInfo())) {
    return (
      <Box p={2} data-testid="tree-builder-denied">
        <Alert severity="info">The tree builder needs the LDS admin role. Ask an LDS admin to change this tree.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/ping-tree")}>
          Back to ping trees
        </Button>
      </Box>
    );
  }
  return <TreeBuilderEditor key={id} />;
};

export default TreeBuilder;
